/** Serialisable view models for the Business Manager Forms page and editor. */

export type EnquiryFormKindValue = 'Enquiry' | 'BookMeeting' | 'Website';
export type EnquiryFormPageTypeValue =
    | 'Project'
    | 'Product'
    | 'Professional'
    | 'Article';
export type EnquiryFormTriggerValue = 'Enquire' | 'BookMeeting';
export type EnquiryAudienceValue = 'Anybody' | 'SpecifiersOnly' | 'NoEnquiries';
export type EnquiryProjectTypeValue =
    | 'Any'
    | 'ResidentialOnly'
    | 'CommercialOnly';

export const ENQUIRY_FORM_PAGE_TYPES: readonly EnquiryFormPageTypeValue[] = [
    'Project',
    'Product',
    'Professional',
    'Article',
];

export const ENQUIRY_FORM_TRIGGERS: readonly EnquiryFormTriggerValue[] = [
    'Enquire',
    'BookMeeting',
];

export const PAGE_TYPE_LABELS: Record<EnquiryFormPageTypeValue, string> = {
    Project: 'Project pages',
    Product: 'Product pages',
    Professional: 'Your profile page',
    Article: 'Article pages',
};

export const TRIGGER_LABELS: Record<EnquiryFormTriggerValue, string> = {
    Enquire: 'Enquire',
    BookMeeting: 'Book a meeting',
};

export const TRIGGER_COLUMN_LABELS: Record<EnquiryFormTriggerValue, string> = {
    Enquire: 'Enquire Call-to-Action',
    BookMeeting: 'Book a Meeting Call-to-Action',
};

export const CTA_LABEL_OPTIONS: readonly { value: string; label: string }[] = [
    { value: 'AutoOptimise', label: 'Auto optimise' },
    { value: 'GetInTouch', label: 'Get in touch' },
    { value: 'RequestMeeting', label: 'Request a meeting' },
    { value: 'RequestQuote', label: 'Request a quote' },
    { value: 'RequestPrice', label: 'Request a price' },
    { value: 'RequestBrochure', label: 'Request a brochure' },
    { value: 'DownloadBrochure', label: 'Download brochure' },
    { value: 'GetTheGuide', label: 'Get the guide' },
    { value: 'DownloadGuide', label: 'Download guide' },
    { value: 'AskAQuestion', label: 'Ask a question' },
    { value: 'MessageSeller', label: 'Message seller' },
    { value: 'BookConsultation', label: 'Book a consultation' },
];

/** Human label for a stored CTA key; `AutoOptimise` and unknown keys return `undefined`. */
export function ctaLabelText(value: string): string | undefined {
    if (value === 'AutoOptimise') return undefined;
    return CTA_LABEL_OPTIONS.find((option) => option.value === value)?.label;
}

export const DESTINATION_OPTIONS = [
    { value: 'Inbox', label: 'Company inbox' },
    { value: 'Teammate', label: 'Teammates' },
    { value: 'Branch', label: 'A branch' },
    { value: 'Calendar', label: 'Book a meeting' },
    { value: 'Message', label: 'Show a message instead' },
] as const;

/** Longest message a "Show a message" rule may display; mirrors the server cap. */
export const MAX_RULE_MESSAGE = 500;

/** What a "Show a message" rule displays when its text is left blank; mirrors the server default. */
export const DEFAULT_RULE_MESSAGE =
    'Thanks for your interest. This is not something we can help with right now.';

export type DestinationValue = (typeof DESTINATION_OPTIONS)[number]['value'];

export type EnquirerTypeValue = 'Homeowner' | 'Specifier' | 'SupplierTrade';
export type ProjectKindValue = 'Residential' | 'Commercial';

export const ENQUIRER_TYPE_OPTIONS: readonly {
    value: EnquirerTypeValue;
    label: string;
}[] = [
    { value: 'Homeowner', label: 'Homeowner' },
    { value: 'Specifier', label: 'Architect or designer' },
    { value: 'SupplierTrade', label: 'Supplier or trade' },
];

export const PROJECT_KIND_OPTIONS: readonly {
    value: ProjectKindValue;
    label: string;
}[] = [
    { value: 'Residential', label: 'Residential' },
    { value: 'Commercial', label: 'Commercial' },
];

/** Question labels as the enquirer sees them; shared by the editor and the preview. */
export const DEFAULT_LOCATION_LABEL = 'Suburb or postcode';

export const FIELD_QUESTIONS = {
    enquiryType: 'What is your enquiry about?',
    enquirerType: 'I am a...',
    projectKind: 'Is this residential or commercial?',
} as const;

/** Custom field input kinds on a Website form; mirrors the PHP enum. */
export const FIELD_TYPE_OPTIONS = [
    { value: 'Text', label: 'Short text' },
    { value: 'Textarea', label: 'Long text' },
    { value: 'Email', label: 'Email' },
    { value: 'Phone', label: 'Phone' },
    { value: 'Url', label: 'Website' },
    { value: 'Select', label: 'Dropdown' },
    { value: 'MultiSelect', label: 'Checkboxes' },
    { value: 'Country', label: 'Country' },
] as const;
export type FieldTypeValue = (typeof FIELD_TYPE_OPTIONS)[number]['value'];
export type FieldWidthValue = 'Half' | 'Full';

/** Types whose choices the owner types in; Country brings its own list. */
export const hasOwnerOptions = (type: FieldTypeValue): boolean =>
    type === 'Select' || type === 'MultiSelect';

/** Types with a fixed list of answers, so there is no placeholder to write. */
export const isChoiceField = (type: FieldTypeValue): boolean =>
    type === 'Select' || type === 'MultiSelect' || type === 'Country';

/** Only a single-pick dropdown can be a routing condition; mirrors `isRoutable()` in PHP. */
export const isRoutableField = (type: FieldTypeValue): boolean =>
    type === 'Select';

/** Label of the extra choice a field shows when "Other" is allowed; the same on the server. */
export const OTHER_OPTION = 'Other';

export const MAX_CUSTOM_FIELDS = 20;
/** Mirrors `EnquiryFormField::MAX_OPTIONS` in PHP. */
export const MAX_FIELD_OPTIONS = 50;

/** Keys the fixed part of every form already owns; a custom field cannot take them. */
export const RESERVED_FIELD_KEYS: readonly string[] = [
    'name',
    'email',
    'phone',
    'message',
    'location',
    'enquiry_type',
    'enquirer_type',
    'project_kind',
    'branch',
];

/**
 * A branch in the organisation network the professional owns: a separate professional
 * that can be offered in the "Which branch?" question and routed to.
 */
export interface BranchOption {
    id: number;
    title: string;
    /** Region URL segment of the branch's first listed location; drives location preselect. */
    regionSegment: string | null;
}

/** What the public form asks when the owner leaves the branch question's wording blank. */
export const DEFAULT_BRANCH_PICKER_LABEL =
    'Which branch would you like to contact?';

/** Mirrors `EnquiryForm::MAX_OFFERED_BRANCHES` in PHP. */
export const MAX_OFFERED_BRANCHES = 50;

/** Mirrors the `BranchPickerLabel` column size in PHP. */
export const MAX_BRANCH_PICKER_LABEL = 120;

export interface EnquiryFormFieldViewModel {
    id: number;
    sort: number;
    key: string;
    label: string;
    type: FieldTypeValue;
    required: boolean;
    placeholder: string;
    options: string[];
    /** Choice fields only: an "Other" choice reveals a text box for a free answer. */
    allowOther: boolean;
    width: FieldWidthValue;
}

/** A condition on one custom Select field: the answer must be one of `values`. */
export interface FieldCondition {
    key: string;
    values: string[];
}

export const THEME_MODES = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'custom', label: 'Custom' },
] as const;
export type ThemeModeValue = (typeof THEME_MODES)[number]['value'];

export interface FormTheme {
    mode: ThemeModeValue;
    background: string;
    fieldBackground: string;
    fieldBorder: string;
    text: string;
    accent: string;
    radius: number;
    fontInherit: boolean;
}

export const LIGHT_THEME: FormTheme = {
    mode: 'light',
    background: 'transparent',
    fieldBackground: '#ffffff',
    fieldBorder: '#e5e5e5',
    text: '#171616',
    accent: '#171616',
    radius: 4,
    fontInherit: false,
};

/**
 * Dark paints its own panel: light text on a transparent background vanishes on the light pages
 * most sites are, so the embed carries the same surface the editor preview shows.
 */
export const DARK_THEME: FormTheme = {
    ...LIGHT_THEME,
    mode: 'dark',
    background: '#111111',
    fieldBackground: '#1f1f1f',
    fieldBorder: '#3a3a3a',
    text: '#f5f5f5',
    accent: '#f5f5f5',
};

/** Resolved tokens for a theme: light and dark ignore the stored colours. */
export function resolveTheme(theme: FormTheme): FormTheme {
    switch (theme.mode) {
        case 'light':
            return {
                ...LIGHT_THEME,
                radius: theme.radius,
                fontInherit: theme.fontInherit,
            };
        case 'dark':
            return {
                ...DARK_THEME,
                radius: theme.radius,
                fontInherit: theme.fontInherit,
            };
        case 'custom':
            return theme;
        default: {
            const exhaustive: never = theme.mode;
            return exhaustive;
        }
    }
}

export type SuccessModeValue = 'Message' | 'Redirect' | 'Downloads';

/** A file or link a form hands out after submit, as the editor sees it. */
export interface EnquiryFormDownloadViewModel {
    id: number;
    title: string;
    sort: number;
    /** Set instead of a file. */
    externalLink: string;
    /** Original file name; empty for link downloads. */
    fileName: string;
    /** Bytes; 0 for link downloads. */
    fileSize: number;
}

/** Upload limits mirrored from `EnquiryFormDownload` in PHP. */
export const FORM_DOWNLOAD_MAX_PER_FORM = 10;
export const FORM_DOWNLOAD_MAX_BYTES = 25 * 1024 * 1024;
export const FORM_DOWNLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.gif';

export function formatFileSize(bytes: number): string {
    if (bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface CrmHints {
    pipeline: string;
    stage: string;
    ownerEmail: string;
    tags: string[];
}

export const EMPTY_CRM_HINTS: CrmHints = {
    pipeline: '',
    stage: '',
    ownerEmail: '',
    tags: [],
};

export function crmHintsEmpty(hints: CrmHints): boolean {
    return (
        hints.pipeline === '' &&
        hints.stage === '' &&
        hints.ownerEmail === '' &&
        hints.tags.length === 0
    );
}

/**
 * Kinds of lead a professional can choose to send on. Mirrors
 * `LeadDeliverySource` in PHP; the labels are the marketer's words for them.
 */
export const LEAD_DELIVERY_SOURCES = [
    {
        value: 'archiproEnquiries',
        label: 'Enquiries on ArchiPro',
        hint: 'Messages and meeting bookings from your ArchiPro profile, projects and products.',
    },
    {
        value: 'websiteForms',
        label: 'Forms on your website',
        hint: 'ArchiPro forms you have embedded on your own site.',
    },
    {
        value: 'downloads',
        label: 'Downloads',
        hint: 'People who download your brochures, CAD files and other documents.',
    },
    {
        value: 'websiteVisitors',
        label: 'Sign-ins on your website',
        hint: 'ArchiPro members who log in or fill a form on your site through the ArchiPro pixel.',
    },
    {
        value: 'events',
        label: 'Events',
        hint: 'Contacts scanned at Home Design Evenings and other ArchiPro events.',
    },
] as const;

export type LeadDeliverySourceValue =
    (typeof LEAD_DELIVERY_SOURCES)[number]['value'];

export const isLeadDeliverySource = (
    value: string,
): value is LeadDeliverySourceValue =>
    LEAD_DELIVERY_SOURCES.some((source) => source.value === value);

export interface LeadDeliveryViewModel {
    webhookEnabled: boolean;
    webhookUrl: string;
    webhookSecretSet: boolean;
    hubspotEnabled: boolean;
    hubspotTokenSet: boolean;
    hubspotDefaultPipeline: string;
    hubspotDefaultOwnerEmail: string;
    pushDeclined: boolean;
    /** Sources being sent; the server returns every key when unrestricted. */
    sources: LeadDeliverySourceValue[];
    anyConfigured: boolean;
}

export interface EnquiryFormTypeViewModel {
    id: number;
    title: string;
    sort: number;
    isActive: boolean;
}

/**
 * One routing rule: every non-empty condition list must match for the rule to apply, and
 * the first applying rule (by sort) wins.
 */
export interface EnquiryFormRoutingRuleViewModel {
    id: number;
    sort: number;
    enquiryFormTypeIds: number[];
    enquirerTypes: EnquirerTypeValue[];
    projectKinds: ProjectKindValue[];
    countries: string[];
    regions: string[];
    /** Branches the visitor may have picked; empty matches any. */
    branchIds: number[];
    destination: DestinationValue;
    calendarSessionId: number | null;
    /** The branch that receives the enquiry when destination is Branch. */
    destinationBranchId: number | null;
    /** Shown instead of sending when destination is Message. */
    message: string | null;
    emails: string[];
    teamMemberIds: number[];
    fieldConditions: FieldCondition[];
    crmHints: CrmHints;
}

export interface RegionOption {
    id: number;
    title: string;
    /** Region URLSegment, what rules store. */
    segment: string;
    parentId: number;
}

export const ROUTING_COUNTRIES: readonly { code: string; label: string }[] = [
    { code: 'NZ', label: 'New Zealand' },
    { code: 'AU', label: 'Australia' },
    { code: 'US', label: 'United States' },
];

export interface EnquiryFormViewModel {
    id: number;
    title: string;
    kind: EnquiryFormKindValue;
    isDefault: boolean;
    audience: EnquiryAudienceValue;
    projectType: EnquiryProjectTypeValue;
    ctaLabel: string;
    customMessage: string;
    messageOverrideEnabled: boolean;
    messagePlaceholder: string;
    /** The form shows a message box; off means the visitor only leaves details. */
    messageEnabled: boolean;
    /** The message box must be filled in. Website forms can switch this off. */
    messageRequired: boolean;
    /** The attach-files control is shown. Website forms set this independently of the message box. */
    attachmentsEnabled: boolean;
    enquiryTypesEnabled: boolean;
    enquirerTypeEnabled: boolean;
    projectKindQuestionEnabled: boolean;
    locationEnabled: boolean;
    routingEnabled: boolean;
    defaultDestination: DestinationValue;
    /** The branch that receives enquiries when defaultDestination is Branch. */
    defaultDestinationBranchId: number | null;
    /** Its name, while it is still in the network; null otherwise. */
    defaultDestinationBranchTitle: string | null;
    defaultTeamMemberIds: number[];
    fallbackRecipientEmails: string[];
    /** The form asks which branch the visitor wants. */
    branchPickerEnabled: boolean;
    branchPickerRequired: boolean;
    /** The owner's wording; empty when they kept the default. */
    branchPickerLabel: string;
    /** Branches offered in the question; empty offers every branch. */
    offeredBranchIds: number[];
    /** Preselect the branch in the visitor's region; they can still change it. */
    branchPreselectByLocation: boolean;
    embedKey: string | null;
    /**
     * Book Meeting forms: the team member the form belongs to. It opens from
     * their profile card and always books with them, so it has no routing.
     */
    ownerTeamMemberId: number | null;
    ownerName: string | null;
    /** Default meeting session, used when defaultDestination is Calendar. */
    calendarSessionId: number | null;
    calendarSessionName: string | null;
    enquiryTypes: EnquiryFormTypeViewModel[];
    routingRules: EnquiryFormRoutingRuleViewModel[];
    /** Website forms only; empty for platform forms. */
    fields: EnquiryFormFieldViewModel[];
    locationLabel: string;
    heading: string;
    submitLabel: string;
    consentText: string;
    theme: FormTheme;
    successMode: SuccessModeValue;
    successMessage: string;
    successUrl: string;
    /** Files offered after submit, in order. */
    downloads: EnquiryFormDownloadViewModel[];
    webhookEnabled: boolean;
    crmHints: CrmHints;
    lastEdited: string | null;
    /** Products that open this form from a button instead of their Product Default. */
    productOverrideCount: number;
    /** Page type / trigger cells currently pointing at this form. */
    assignedTo: {
        pageType: EnquiryFormPageTypeValue;
        trigger: EnquiryFormTriggerValue;
    }[];
}

export interface EnquiryFormAssignmentViewModel {
    pageType: EnquiryFormPageTypeValue;
    trigger: EnquiryFormTriggerValue;
    /** Null when the CTA is switched off for this page type. */
    formId: number | null;
}

export interface RecipientFace {
    id: string;
    name: string;
    imageUrl: string | null;
    /** Company logo is a mark; people are circular headshots. */
    kind: 'company' | 'person';
}

export interface TeamMemberOption {
    id: number;
    name: string;
    imageUrl: string | null;
    /** Shown on the public profile; only public members can own a booking form. */
    isPublic?: boolean;
    /** Whether their profile card offers Book a meeting. */
    bookingEnabled?: boolean;
}

export interface CalendarSessionOption {
    id: number;
    name: string;
    isActive: boolean;
    assignees: TeamMemberOption[];
}

export interface BusinessManagerFormsViewModel {
    professionalId: number;
    professionalTitle: string;
    professionalUrlSegment: string;
    professionalCountry: string;
    /** Supplies products: the automatic profile button asks for a price or brochure. */
    professionalIsSupplier: boolean;
    isLive: boolean;
    /**
     * The viewer may create, edit, delete and re-assign forms (see `canManageForms`). Off shows
     * the page read-only.
     */
    canManage: boolean;
    forms: EnquiryFormViewModel[];
    assignments: EnquiryFormAssignmentViewModel[];
    /** Products whose own buttons don't follow the Product Default: another form, or hidden. */
    customisedProductCount: number;
    teamMembers: TeamMemberOption[];
    calendarSessions: CalendarSessionOption[];
    /** Region options per ROUTING_COUNTRIES code, for location rules. */
    regionsByCountry: Record<string, RegionOption[]>;
    /** Branches in the network the professional owns, by name; empty when it owns none. */
    networkBranches: BranchOption[];
    /** Company mark shown on the Company inbox destination. */
    companyLogoUrl: string | null;
    /** The colour behind the logo, as the public site draws it. */
    companyLogoBackground: string | null;
    /** The profile's banner photos in hero order, one per page-type card; empty falls back to stock. */
    profileBannerUrls: string[];
    /** The photo the profile page puts behind its popups (its first non-About banner). */
    profileHeroUrl: string | null;
    /** Professional-level notification list, shown as the routing fallback. */
    notificationEmails: string[];
    /** Absolute origin used to build embed snippets, e.g. https://archipro.co.nz */
    siteOrigin: string;
    /**
     * Webhook / HubSpot settings; null when the viewer cannot manage the professional or the
     * bm_forms_crm flag is off, which hides every CRM setting on the page and in the editor.
     */
    leadDelivery: LeadDeliveryViewModel | null;
}
